import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { LayoutService } from 'app/core/http/services/layout.service';
import { Observable, throwError } from 'rxjs';
import { retry, catchError, map } from 'rxjs/operators';
import { environment } from 'app/environments/environment';
import { procedureI } from 'app/core/interfaces/procedure';
import { HealthCareServiceAPIResponse, Healthcareservice, HealthcareserviceSpecialists } from 'app/core/interfaces/healthcareservice';
import { HealthcareServiceAdapter } from 'app/core/adapters/healthcareservice.adapter';

@Injectable({
  providedIn: 'root',
})
export class ProcedureService {
  constructor(private http: HttpClient, private readonly layout: LayoutService, private healthcareserviceAdapter: HealthcareServiceAdapter) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('userTokenId');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  public getProcedures(): Observable<any[]> {
    return this.http
      .get<any[]>(environment.apiUrl + '/secretarias-healthcareservice', {
        headers: this.getHeaders(),
      })
      .pipe(
        retry(1),
        catchError((error) => {
          let errorMessage = '';
          if (error.error instanceof ErrorEvent) {
            errorMessage = error.error.message;
          } else {
            errorMessage = error;
          }
          this.layout.openDialog({
            title: 'Erro',
            content: errorMessage,
            showButtonCancel: false,
          });
          return throwError(error);
        })
      );
  }

  list(): Observable<Healthcareservice[]> {
    return this.http
      .get<HealthCareServiceAPIResponse[]>(`${environment.apiUrl}/secretarias-healthcareservice`)
      .pipe(
        map((organization: HealthCareServiceAPIResponse[]) =>
          organization.map((item: HealthCareServiceAPIResponse) => this.healthcareserviceAdapter.adapterToModel(item))
        )
      );
  }

  listNearest(params: HttpParams): Observable<Healthcareservice[]> {
    return this.http
      .get<HealthCareServiceAPIResponse[]>(`${environment.apiUrl}/secretarias-healthcareservice/nearest`, { params: params })
      .pipe(
        map((organization: HealthCareServiceAPIResponse[]) =>
          organization.map((item: HealthCareServiceAPIResponse) => this.healthcareserviceAdapter.adapterToModel(item))
        )
      );
  }

  listNearestBySpecialists(params: HttpParams): Observable<HealthcareserviceSpecialists[]> {
    return this.http.get<HealthcareserviceSpecialists[]>(`${environment.apiUrl}/secretarias-healthcareservice/nearest/specialists`, {
      params: params,
    });
  }

  public showById(id: string) {
    return this.http.get<Healthcareservice>(`${environment.apiUrl}/secretarias-healthcareservice/${id}`);
  }

  show(id: string): Observable<Healthcareservice> {
    return this.http
      .get<HealthCareServiceAPIResponse>(`${environment.apiUrl}/secretarias-healthcareservice/${id}`)
      .pipe(map((healthcareservice: HealthCareServiceAPIResponse) => this.healthcareserviceAdapter.adapterToModel(healthcareservice)));
  }

  public getProcedureDetail(id: string): Observable<procedureI> {
    return this.http
      .get<procedureI>(environment.apiUrl + 'procedure/' + id, {
        headers: this.getHeaders(),
      })
      .pipe(
        retry(1),
        catchError((error) => {
          let errorMessage = '';
          if (error.error instanceof ErrorEvent) {
            errorMessage = error.error.message;
          } else {
            errorMessage = error;
          }
          this.layout.openDialog({
            title: 'Erro',
            content: errorMessage,
            showButtonCancel: false,
          });
          return throwError(error);
        })
      );
  }

  public getTussByPrefix(params: HttpParams){

    return this.http.get<any[]>(`${environment.apiUrl}/secretarias-healthcareservice/tuss`, {
          params: params,
    });
  }

  public updateProcedure(id: string, data: any): Observable<procedureI> {
    return this.http.put<procedureI>(`${environment.apiUrl}/secretarias-healthcareservice/${id}`, data, { headers: this.getHeaders() }).pipe(
      retry(1),
      catchError((error) => {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          errorMessage = error.error.message;
        } else {
          errorMessage = error;
        }
        this.layout.openDialog({
          title: 'Erro',
          content: errorMessage,
          showButtonCancel: false,
        });
        return throwError(error);
      })
    );
  }

  //ajustar parametros para o model do fhir
  public createProcedure(data: any): Observable<any> {
    return this.http
      .post<any>(environment.apiUrl + '/secretarias-healthcareservice', data, {
        headers: this.getHeaders(),
      })
      .pipe(
        retry(1),
        catchError((error) => {
          let errorMessage = '';
          if (error.error instanceof ErrorEvent) {
            errorMessage = error.error.message;
          } else {
            errorMessage = error;
          }
          this.layout.openDialog({
            title: 'Erro',
            content: errorMessage,
            showButtonCancel: false,
          });
          return throwError(error);
        })
      );
  }

  public deleteProcedure(id: string) {
    return this.http.delete<any>(`${environment.apiUrl}/secretarias-healthcareservice/${id}`, { headers: this.getHeaders() }).pipe(
      retry(1),
      catchError((error) => {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          errorMessage = error.error.message;
        } else {
          errorMessage = error;
        }
        this.layout.openDialog({
          title: 'Erro',
          content: errorMessage,
          showButtonCancel: false,
        });
        return throwError(error);
      })
    );
  }

  public create(healthcareservice: Healthcareservice): Observable<Healthcareservice> {
    const healthcareserviceFHIR = this.healthcareserviceAdapter.adaptToAPIFormat(healthcareservice);

    return this.http.post<HealthCareServiceAPIResponse>(`${environment.apiUrl}/secretarias-healthcareservice`, healthcareserviceFHIR).pipe(
      map((item: HealthCareServiceAPIResponse) => this.healthcareserviceAdapter.adapterToModel(item)),
      retry(1),
      catchError((error) => {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          errorMessage = error.error.message;
        } else {
          errorMessage = error;
        }
        this.layout.openDialog({
          title: 'Erro',
          content: errorMessage,
          showButtonCancel: false,
        });
        return throwError(error);
      })
    );
  }

  public update(id: string, healthcareservice: Healthcareservice): Observable<Healthcareservice> {
    const healthcareserviceFHIR = this.healthcareserviceAdapter.adaptToAPIFormat(healthcareservice);

    return this.http.put<HealthCareServiceAPIResponse>(`${environment.apiUrl}/secretarias-healthcareservice/${id}`, healthcareserviceFHIR).pipe(
      map((item: HealthCareServiceAPIResponse) => this.healthcareserviceAdapter.adapterToModel(item)),
      retry(1),
      catchError((error) => {
        let errorMessage = '';
        if (error.error instanceof ErrorEvent) {
          errorMessage = error.error.message;
        } else {
          errorMessage = error;
        }
        this.layout.openDialog({
          title: 'Erro',
          content: errorMessage,
          showButtonCancel: false,
        });
        return throwError(error);
      })
    );
  }

  public listByOrganizations(organization_id: string): Observable<Healthcareservice[]> {
    return this.http
      .get<HealthCareServiceAPIResponse[]>(`${environment.apiUrl}/secretarias-healthcareservice/organizations/${organization_id}`)
      .pipe(
        map((organization: HealthCareServiceAPIResponse[]) =>
          organization.map((item: HealthCareServiceAPIResponse) => this.healthcareserviceAdapter.adapterToModel(item))
        )
      );
  }

  public updateOld(id: string, params: any) {
    return this.http.put(`${environment.apiUrl}/secretarias-healthcareservice/${id}`, params);
  }
}
